// src/components/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Plant, plantsAPI, botanicalAPI, BotanicalCharacteristic } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  AppBar,
  Toolbar
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Visibility as ViewIcon,
  CalendarToday as DateIcon,
  LocalFlorist as PlantIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [filteredPlants, setFilteredPlants] = useState<Plant[]>([]);
  const [characteristics, setCharacteristics] = useState<BotanicalCharacteristic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'family' | 'date'>('name');

  useEffect(() => {
    loadData();
  }, []);

  // Filter and sort plants based on search term and sort option
  useEffect(() => {
    let filtered = plants;

    // Filter by search term
    if (searchTerm) {
      filtered = plants.filter(plant => 
        plant.primary_scientific_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.family.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.genus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        plant.species?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort plants
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.primary_scientific_name.localeCompare(b.primary_scientific_name);
        case 'family':
          return a.family.localeCompare(b.family);
        case 'date':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    setFilteredPlants(filtered);
  }, [plants, searchTerm, sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plantsResponse, characteristicsResponse] = await Promise.all([
        plantsAPI.getPlants(),
        botanicalAPI.getCharacteristics()
      ]);
      
      setPlants(plantsResponse.plants || []);
      setCharacteristics(characteristicsResponse.characteristics || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        bgcolor="grey.50"
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <PlantIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            PalmGarden Dashboard
          </Typography>
          <Button color="inherit" component={Link} to="/plants" sx={{ mr: 1 }}>
            Plant Management
          </Button>
          {user?.role === 'super-admin' && (
            <Button color="inherit" component={Link} to="/admin" sx={{ mr: 1 }}>
              Admin Management
            </Button>
          )}
          <Typography variant="body2" sx={{ mr: 2 }}>
            Welcome, {user?.display_name || user?.username} ({user?.role})
          </Typography>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary">
          PalmGarden Dashboard
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error: {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Plants Section */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom color="primary">
                🌿 Plant Collection ({filteredPlants.length} of {plants.length})
              </Typography>
          
              {/* Search and Filter Controls */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                      fullWidth
                      label="Search Plants"
                      placeholder="Search by name, family, genus, or species..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sort By</InputLabel>
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as 'name' | 'family' | 'date')}
                        label="Sort By"
                      >
                        <MenuItem value="name">Sort by Name</MenuItem>
                        <MenuItem value="family">Sort by Family</MenuItem>
                        <MenuItem value="date">Sort by Date</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                {filteredPlants.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    {searchTerm ? (
                      <>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          🔍 No plants found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          No plants match your search for "{searchTerm}"
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={() => setSearchTerm('')}
                          startIcon={<SearchIcon />}
                        >
                          Clear Search
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body1" color="text.secondary">
                        No plants found
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {filteredPlants.map((plant) => (
                      <Grid size={{ xs: 12 }} key={plant.id}>
                        <Card 
                          sx={{ 
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 4
                            }
                          }}
                        >
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Typography variant="h6" component="h3" color="primary" fontWeight="bold">
                                {plant.primary_scientific_name}
                              </Typography>
                              <Chip 
                                label={plant.status}
                                size="small"
                                color={plant.status === 'published' ? 'success' : 'warning'}
                                variant="outlined"
                              />
                            </Box>
                            
                            <Grid container spacing={1} sx={{ mb: 2 }}>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Family:
                                </Typography>
                                <Typography variant="body2">
                                  {plant.family}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Genus:
                                </Typography>
                                <Typography variant="body2">
                                  {plant.genus || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Species:
                                </Typography>
                                <Typography variant="body2">
                                  {plant.species || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 6 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Size:
                                </Typography>
                                <Typography variant="body2">
                                  {plant.size_category || 'N/A'}
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {plant.description && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  mb: 2,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {plant.description}
                              </Typography>
                            )}
                          </CardContent>

                          <Divider />
                          
                          <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                            <Box display="flex" alignItems="center">
                              <DateIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                Added: {new Date(plant.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Button 
                              size="small" 
                              variant="contained" 
                              startIcon={<ViewIcon />}
                              onClick={() => navigate(`/plants/${plant.id}`)}
                            >
                              View Details
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Botanical Characteristics Section */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom color="primary">
                Botanical Characteristics ({characteristics.length})
              </Typography>
              <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                {characteristics.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No characteristics found
                  </Typography>
                ) : (
                  Object.entries(
                    characteristics.reduce((acc, char) => {
                      if (!acc[char.category]) acc[char.category] = [];
                      acc[char.category].push(char);
                      return acc;
                    }, {} as Record<string, BotanicalCharacteristic[]>)
                  ).map(([category, chars]) => (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography variant="h6" color="primary" sx={{ textTransform: 'capitalize', mb: 1 }}>
                        {category.replace(/_/g, ' ')}
                      </Typography>
                      {chars.map((char) => (
                        <Card key={char.id} sx={{ mb: 1, p: 1 }}>
                          <Typography variant="body2">
                            <strong>{char.value}</strong>
                            {char.description && (
                              <span style={{ color: '#666' }}>
                                {' '}- {char.description}
                              </span>
                            )}
                          </Typography>
                        </Card>
                      ))}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* API Test Section */}
        <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom color="primary">
            API Test Results
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ✅ Authentication
                  </Typography>
                  <Typography variant="body2">
                    Login/logout working
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ✅ Plants API
                  </Typography>
                  <Typography variant="body2">
                    {plants.length} plants loaded
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ✅ Botanical API
                  </Typography>
                  <Typography variant="body2">
                    {characteristics.length} characteristics loaded
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ✅ Role-Based Access
                  </Typography>
                  <Typography variant="body2">
                    User role: {user?.role}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;